import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Dialog unifié de comparaison de joueurs.
 *
 * Props :
 *   - open, onClose, onValidate
 *   - searchText, setSearchText
 *   - searchResults : liste de joueurs filtrée (par le parent)
 *   - playersToCompare, togglePlayerComparison, isPlayerSelected (du contexte)
 *   - mediaUrl, timestamp
 *   - labels : { title, searchPlaceholder, selectedSingular, selectedPlural, cancel, validate }
 */
const PlayerCompareDialog = ({
  open,
  onClose,
  onValidate,
  searchText,
  setSearchText,
  searchResults,
  playersToCompare,
  togglePlayerComparison,
  isPlayerSelected,
  mediaUrl,
  timestamp,
  labels,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{labels.title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={labels.searchPlaceholder}
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />

        {/* Chips des joueurs sélectionnés */}
        {playersToCompare.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
              {playersToCompare.length === 1
                ? `1 ${labels.selectedSingular}`
                : `${playersToCompare.length} ${labels.selectedPlural}`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {playersToCompare.map((player) => (
                <Chip
                  key={player.id}
                  label={player.nom || player.name || `Jugador ${player.id}`}
                  onDelete={() => togglePlayerComparison(player)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
          {searchResults.map((player) => {
            const labelId = `compare-checkbox-${player.id}`;
            const checked = isPlayerSelected(player.id);
            return (
              <ListItem
                key={player.id}
                button
                onClick={() => togglePlayerComparison(player)}
                secondaryAction={<Checkbox edge="end" checked={checked} />}
              >
                <ListItemAvatar>
                  <Avatar
                    src={`${mediaUrl}/photos/${player.id}.png?${timestamp}`}
                    alt={player.nom}
                  />
                </ListItemAvatar>
                <ListItemText id={labelId} primary={player.nom} />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {labels.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={onValidate}
          disabled={playersToCompare.length < 2}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {labels.validate}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlayerCompareDialog;
